/**
 * Right-click context menu for endpoint nodes on the infrastructure map.
 * Pure render component -- caller owns positioning and action wiring.
 */

import type { ReactElement } from 'react'
import type { TileSize } from '../../../types/infrastructure-map'

export interface NodeContextMenuState {
  x: number
  y: number
  endpointId: string
  currentSize: TileSize
  isInMapSet: boolean
  isInCollectionSet: boolean
  selectedCount: number
}

interface NodeContextMenuProps {
  menu: NodeContextMenuState
  onChangeTileSize: (size: TileSize) => void
  onAddToMapSet: () => void
  onRemoveFromMapSet: () => void
  onAddSelectedToCollectionSet: () => void
  onRemoveFromCollectionSet: () => void
  onAddToCollectionSet: () => void
  onViewDetails: () => void
}

const TILE_SIZES: TileSize[] = ['dot', 'small', 'medium', 'large']

export function NodeContextMenu({
  menu,
  onChangeTileSize,
  onAddToMapSet,
  onRemoveFromMapSet,
  onAddSelectedToCollectionSet,
  onRemoveFromCollectionSet,
  onAddToCollectionSet,
  onViewDetails,
}: NodeContextMenuProps): ReactElement {
  return (
    <div
      className="fixed z-50 min-w-[160px] rounded-lg border border-slate-200 bg-white py-1 shadow-xl dark:border-slate-700 dark:bg-slate-800"
      style={{ left: menu.x, top: menu.y }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      <div className="px-3 py-1.5 text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Tile Size
      </div>
      {TILE_SIZES.map((size) => (
        <TileSizeOption
          key={size}
          size={size}
          isCurrent={menu.currentSize === size}
          onClick={() => {
            onChangeTileSize(size)
          }}
        />
      ))}

      <Divider />

      {menu.isInMapSet ? (
        <MenuButton onClick={onRemoveFromMapSet}>Remove from Map</MenuButton>
      ) : (
        <MenuButton onClick={onAddToMapSet}>Add to Map</MenuButton>
      )}

      <Divider />

      <div className="px-3 py-1.5 text-xs font-medium tracking-wider text-slate-500 uppercase dark:text-slate-400">
        Collection
      </div>
      {menu.selectedCount > 1 ? (
        <MenuButton onClick={onAddSelectedToCollectionSet}>
          Add {String(menu.selectedCount)} Selected to Collection
        </MenuButton>
      ) : menu.isInCollectionSet ? (
        <MenuButton onClick={onRemoveFromCollectionSet}>Remove from Collection</MenuButton>
      ) : (
        <MenuButton onClick={onAddToCollectionSet}>Add to Collection</MenuButton>
      )}

      <Divider />

      <MenuButton onClick={onViewDetails}>View Details</MenuButton>
    </div>
  )
}

function TileSizeOption({
  size,
  isCurrent,
  onClick,
}: {
  size: TileSize
  isCurrent: boolean
  onClick: () => void
}): ReactElement {
  const label = size === 'dot' ? 'Dot (minimal)' : size
  const className = `w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-700 ${
    isCurrent
      ? 'text-fuchsia-600 dark:text-fuchsia-400 font-medium'
      : 'text-slate-700 dark:text-slate-300'
  }`
  return (
    <button onClick={onClick} className={className}>
      <span className="capitalize">{label}</span>
      {isCurrent && <span className="text-fuchsia-500">✓</span>}
    </button>
  )
}

function MenuButton({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}): ReactElement {
  return (
    <button
      onClick={onClick}
      className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
    >
      {children}
    </button>
  )
}

function Divider(): ReactElement {
  return <div className="my-1 border-t border-slate-200 dark:border-slate-700" />
}
